import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Power, SignOut } from "@phosphor-icons/react";
import { NavLink } from "react-router-dom";
import PredictivePill from "./PredictivePill";

/**
 * Top-level navigation. Each entry maps to one page-level domain. New
 * exchanges or features should *not* add buttons here unless they earn
 * their own dedicated section.
 */
const NAV_ITEMS = [
  { to: "/",          labelKey: "menuDashboard",  testid: "nav-dashboard"  },
  { to: "/liquidity", labelKey: "menuLiquidity",  testid: "nav-liquidity"  },
  { to: "/arbitrage", labelKey: "menuArbitrage",  testid: "nav-arbitrage"  },
];

function NavMenu({ t }) {
  return (
    <nav
      className="hidden md:flex items-center gap-1 pl-4 border-l"
      style={{ borderColor: "var(--mq-border)" }}
      data-testid="main-nav"
    >
      {NAV_ITEMS.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === "/"}
          data-testid={it.testid}
          className={({ isActive }) =>
            `mq-btn ${isActive ? "mq-btn-primary" : ""}`
          }
          style={{ padding: "0.35rem 0.7rem", fontSize: "0.7rem" }}
        >
          {t(it.labelKey)}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Header({ state }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();

  const running = state?.running;
  const halted = state?.halted;
  const regime = state?.regime?.regime || "—";
  const equity = state?.equity_usd ?? 0;
  const pnl = state?.risk?.daily_pnl_usd ?? 0;
  const predictive = state?.predictive;

  const statusLabel = halted ? t("botHalted") : running ? t("botRunning") : t("botStopped");
  const statusColor = halted ? "var(--mq-red)" : running ? "var(--mq-green)" : "var(--mq-text-2)";

  return (
    <header
      className="sticky top-0 z-50 bg-black border-b"
      style={{ borderColor: "var(--mq-border)" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4 px-3 sm:px-6 py-3 overflow-x-auto">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/marqolix-logo.png"
              alt="Marqolix"
              data-testid="header-logo"
              className="h-8 w-8 object-contain"
            />
            <Power size={18} weight="bold" style={{ color: statusColor }} />
            <span className="mq-heading text-lg font-bold tracking-tighter">
              MARQOLIX <span className="text-[var(--mq-text-2)]">NG</span>
            </span>
            <span className="mq-label hidden sm:inline">{t("version")}</span>
          </div>
          <div className="hidden md:flex items-center gap-2 pl-4 border-l" style={{ borderColor: "var(--mq-border)" }}>
            <span
              className="mq-status-dot"
              style={{ background: statusColor, boxShadow: running ? `0 0 8px ${statusColor}` : "none" }}
            />
            <span data-testid="header-bot-status" className="mq-label" style={{ color: statusColor }}>
              {statusLabel}
            </span>
            {state?.testnet && (
              <span className="mq-label ml-2 px-2 py-0.5 border" style={{ borderColor: "var(--mq-yellow)", color: "var(--mq-yellow)" }}>
                TESTNET
              </span>
            )}
          </div>

          <NavMenu t={t} />
        </div>

        <div className="flex items-center gap-3 sm:gap-6">
          <div className="hidden sm:block">
            <PredictivePill predictive={predictive} />
          </div>
          <div className="hidden sm:flex flex-col items-end">
            <span className="mq-label">{t("regime")}</span>
            <span data-testid="header-regime" className="mq-metric text-sm" style={{ color: "var(--mq-cyan)" }}>
              [ {regime} ]
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="mq-label">{t("equity")}</span>
            <span data-testid="header-equity" className="mq-metric text-sm">
              ${equity.toLocaleString()}
            </span>
          </div>
          <div className="hidden sm:flex flex-col items-end">
            <span className="mq-label">{t("pnl")}</span>
            <span
              data-testid="header-pnl"
              className="mq-metric text-sm"
              style={{ color: pnl >= 0 ? "var(--mq-green)" : "var(--mq-red)" }}
            >
              {pnl >= 0 ? "+" : ""}
              {pnl.toFixed(2)}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              data-testid="language-toggle-en"
              className={`mq-btn ${lang === "en" ? "mq-btn-primary" : ""}`}
              style={{ padding: "0.25rem 0.55rem", fontSize: "0.65rem" }}
              onClick={() => setLang("en")}
            >EN</button>
            <button
              data-testid="language-toggle-fr"
              className={`mq-btn ${lang === "fr" ? "mq-btn-primary" : ""}`}
              style={{ padding: "0.25rem 0.55rem", fontSize: "0.65rem" }}
              onClick={() => setLang("fr")}
            >FR</button>
          </div>

          <button
            data-testid="logout-button"
            onClick={logout}
            className="mq-btn flex items-center gap-1"
            style={{ padding: "0.35rem 0.6rem" }}
            title={user?.email}
          >
            <SignOut size={14} />
            <span className="hidden md:inline">{t("logout")}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
